import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import './index.scss';
import { useRedux } from 'services/gecko-store/redux';
import { GeckoToastService } from 'ui';
import { renderError } from 'services/gecko-error';
import useProgressFileUpload from 'components/useProgressFileUpload';

function DragUpload(props) {
  const [isDragging, setIsDragging] = useState(false);

  const uploadURL = useRedux(
    (state) => props.uploadURL || `${state.uploadURL}/files/upload`
  );
  const defaultHeaders = useRedux((state) => state.appState.defaultHeaders);

  const { isUploading, uploadProgress, startUpload } = useProgressFileUpload({
    uploadURL,
    requestHeaders: defaultHeaders,
  });

  useEffect(() => {
    const stopWindowDragover = (event) => event.preventDefault();
    window.addEventListener('dragover', stopWindowDragover);
    window.addEventListener('drop', stopWindowDragover);
    return () => {
      window.removeEventListener('dragover', stopWindowDragover);
      window.removeEventListener('drop', stopWindowDragover);
    };
  }, []);

  useEffect(() => {
    props.onProgress({ isUploading, uploadProgress });
  }, [isUploading, uploadProgress]);

  const defaultCancelledHandler = (handler) => (event) => {
    event.preventDefault();
    event.stopPropagation();
    return handler(event);
  };

  const dragEnter = defaultCancelledHandler((event) => {
    const isDraggingFiles = event.dataTransfer.types.includes('Files');
    if (!isDraggingFiles || isUploading) {
      return;
    }
    setIsDragging(true);
  });

  const dragLeave = defaultCancelledHandler(() => setIsDragging(false));

  const dropHandler = defaultCancelledHandler((event) => {
    setIsDragging(false);

    const files = event.dataTransfer.files;
    if (!files.length) {
      return console.error('No files selected');
    }

    props.onDrop(files);

    if (props.autoUpload) {
      startUpload(files)
        .then(JSON.parse)
        .then((responseData) => props.onUpload({ ...responseData, files }))
        .catch((error) => {
          try {
            renderError(JSON.parse(error));
          } catch (_) {
            new GeckoToastService().error(error);
          }
        });
    }
  });

  const overlayClasses = `${isDragging || isUploading ? 'drag-started' : ''}`;
  const showProgressBar = isUploading && !props.hideProgress;
  return (
    <div
      className="DragUpload"
      onDragEnter={dragEnter}
      onDragOver={dragEnter}
      onDrop={dropHandler}
    >
      <div
        className={`DragUpload-overlay ${overlayClasses}`}
        onDragLeave={dragLeave}
        onDrop={dropHandler}
      >
        <div>
          <i className="far fa-download"></i>
          <span>
            {isUploading ? 'Uploading file' : 'Drop a file to upload'}
          </span>
          {showProgressBar ? (
            <progress
              className="DragUpload-progress"
              max={100}
              value={uploadProgress}
            />
          ) : null}
        </div>
      </div>
      {props.children}
    </div>
  );
}

DragUpload.propTypes = {
  children: PropTypes.element,
  uploadURL: PropTypes.string,
  onUpload: PropTypes.func,
  hideProgress: PropTypes.bool,
  onProgress: PropTypes.func,
  autoUpload: PropTypes.bool,
};

DragUpload.defaultProps = {
  uploadURL: process.env.REACT_APP_API_URL,
  onProgress: () => {},
  onUpload: () => {},
  onDrop: () => {},
  hideProgress: false,
  autoUpload: true,
};

DragUpload.displayName = 'DragUpload';

export default DragUpload;
